import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import {
  BoundingBox,
  HighlightData,
} from 'src/components/molecules/OCRHighlightOverlay/types'
import {
  isSafeImageUrl,
  ReportMessage,
  reportTypeEnum,
} from 'src/components/molecules/ReportMessages'
import { Option } from 'src/components/molecules/SelectWithGroupOption'
import { ZoomableReceiptImageProps } from 'src/components/molecules/ZoomableReceiptImage'
import { isHiddenCalculationItemByPolicy } from 'src/domain/calculationItem'
import { hasNoManualInputStep, isMemoImageHidden } from 'src/domain/org'
import CalculationDSL from 'src/slices/calculation/calculation_dsl'
import { parseCalculationDSLsToText } from 'src/slices/calculation/parser'
import { PutDefiniteValue } from 'src/slices/definiteValues/definiteValuesSlice'
import {
  BoundingBox as ApiBoundingBox,
  CalculationDSLDefiniteValue,
  OCRFormatDefiniteValue,
  ReceiptImage,
  SalesReportCalculationResult,
  SalesReportOCRFormatResult,
  TenantRegisterSalesReport,
  TenantResentRegister,
  TenantSalesReport,
} from 'src/slices/services/api'
import { DATE_FORMAT, formatDateTime } from 'src/utils/date'
import { isNumber } from 'src/utils/number'
import { Cell, ChildRow } from '../../components/TenantSalesReportTableRow'
import {
  FormTypesTenantSalesReport,
  FormNameEnum,
  Register,
  RowWithSum,
} from '../../type'

const convertValue = (value?: number | null): string => {
  return typeof value === 'number' ? value.toLocaleString() : '-'
}

// 描画側（ReportMessages の safeImagesOf）と同じフィルタで判定し、
// 「画像ありと判定したのに描画時に全件除外され空のカードになる」食い違いを防ぐ
const hasImages = (images?: string[]): boolean =>
  !!images && images.some(isSafeImageUrl)

// 補足事項は本文または画像のどちらか一方だけでも表示対象にする。
// 通常報告・再送信で判定がズレないよう共通化する。
const shouldDisplayMessage = (text?: string, images?: string[]): boolean =>
  !!text || hasImages(images)

const getReadItemDefaultValue = (
  calcInputVal: number | null,
  calcOcrVal: number | null,
  readOcrVal: number | null,
  isManualInputStepMissing: boolean,
  definiteValue?: number
): number => {
  // NOTE: 確定値があればそれを返す
  if (definiteValue !== undefined) return definiteValue
  // NOTE: 手入力ステップが無い org は手入力値が常に null のため、算出項目の OCR 値があると
  // 下の一致判定が成立せず 0 になってしまう。手入力値との照合をせず読取項目の OCR 値を返す (Issue #3687)
  if (isManualInputStepMissing && readOcrVal !== null) return readOcrVal
  // NOTE: 算出項目の手入力値と OCR 値が一致すれば、 読取項目の OCR 値を返す。
  // 両方とも null なら 0 を返す
  if (calcInputVal === calcOcrVal && readOcrVal !== null) return readOcrVal

  return 0
}

const getCalculationDefaultValue = (
  inputVal: number | null,
  ocrVal: number | null,
  definiteValue?: number
): number => {
  // NOTE: 確定値があればそれを返す
  if (definiteValue !== undefined) return definiteValue
  // NOTE: ocr値より手入力値が優先される
  if (inputVal !== null) return inputVal
  if (ocrVal !== null) return ocrVal

  // NOTE: 両方とも null なら初期値に 0 を返す
  return 0
}

// 組織ごとの非表示項目であるか判定する
const filterHiddenCalculationItems = (
  results: SalesReportCalculationResult[],
  orgCode: string
): SalesReportCalculationResult[] => {
  return results.filter(
    (result) =>
      !isHiddenCalculationItemByPolicy(orgCode, result.calculationItem)
  )
}

// NOTE: 確定値と手入力値を比較し、差分があるか判定する
export const hasDifference = (
  definiteValue: number | undefined,
  manualInputValue: number | null
): boolean => {
  if (definiteValue === undefined) {
    return false
  }
  // 手入力値が無い場合、確定値が0以外の場合のみ差分ありと判定
  if (manualInputValue === null) {
    return definiteValue !== 0
  }

  return definiteValue !== manualInputValue
}

const isValidBoundingBox = (
  box: ApiBoundingBox | undefined
): box is BoundingBox => {
  if (!box || !Array.isArray(box.vertices)) return false
  return box.vertices.length === 4
}

// colorIndex には連番を割り当て、% HIGHLIGHT_COLORS.length のラップは描画側で行う
// rowColorIndices のキーは calculationItem.id を使用し、配列インデックスへの依存を排除する
const extractHighlightsWithColorIndex = (
  results: SalesReportCalculationResult[]
): {
  highlights: HighlightData[]
  rowColorIndices: Map<string, number>
} => {
  const highlights: HighlightData[] = []
  const rowColorIndices = new Map<string, number>()
  let colorCounter = 0

  results.forEach((result) => {
    const rowHighlights: Omit<HighlightData, 'colorIndex'>[] = []
    result.ocrFormatResults.forEach((ocrResult) => {
      const readItemBox = isValidBoundingBox(ocrResult.readItemBox)
        ? ocrResult.readItemBox
        : null
      const valueBox = isValidBoundingBox(ocrResult.valueBox)
        ? ocrResult.valueBox
        : null
      const imageIndex = ocrResult.imageIndex ?? null

      if (readItemBox || valueBox) {
        rowHighlights.push({
          ocrFormatId: ocrResult.ocrFormat.id,
          readItem: ocrResult.ocrFormat.readItem,
          value: ocrResult.result.ocrValue,
          readItemBox,
          valueBox,
          imageIndex,
        })
      }
    })

    if (rowHighlights.length > 0) {
      const colorIndex = colorCounter
      rowColorIndices.set(result.calculationItem.id, colorIndex)
      rowHighlights.forEach((h) => highlights.push({ ...h, colorIndex }))
      colorCounter++
    }
  })
  return { highlights, rowColorIndices }
}

interface TenantSalesReportPresenter {
  convertToRegisters(
    reports: TenantRegisterSalesReport[],
    orgCode: string
  ): Register[]
  convertToDefiniteValues(
    tenantSalesReports: TenantRegisterSalesReport[],
    inputValues: FormTypesTenantSalesReport,
    orgCode: string
  ): PutDefiniteValue[]
  validateInputValues(inputValues: FormTypesTenantSalesReport): boolean
  genReportMessages(
    report: TenantSalesReport | undefined,
    orgCode: string
  ): ReportMessage[]
  genSelectDateOptions(report?: TenantSalesReport): Option[]
  findSelectedResentImage(
    id: string,
    report?: TenantSalesReport
  ): ReceiptImage | undefined
  convertResentImages(
    reports: TenantResentRegister[]
  ): ZoomableReceiptImageProps[]
}

const Presenter: TenantSalesReportPresenter = {
  convertToRegisters(reports, orgCode) {
    const placeholder = 'ご入力ください'
    const isManualInputStepMissing = hasNoManualInputStep(orgCode)

    const convertToCells = (result: SalesReportCalculationResult): Cell[] => {
      return [
        {
          key: 'name',
          title: result.calculationItem.name,
        },
        {
          key: 'input',
          title: convertValue(result.results.manualInputValue),
        },
        {
          key: 'ocr',
          title: convertValue(result.results.ocrValue),
        },
      ]
    }

    const convertChildCells = (result: SalesReportOCRFormatResult): Cell[] => {
      return [
        {
          key: 'name',
          title: result.ocrFormat.readItem,
        },
        {
          key: 'input',
          title: convertValue(result.result.manualInputValue),
        },
        {
          key: 'ocr',
          title: convertValue(result.result.ocrValue),
        },
      ]
    }

    const convertChildRows = (
      registerIndex: number,
      rowIndex: number,
      result: SalesReportCalculationResult
    ): ChildRow[] => {
      return result.ocrFormatResults.map((ocrResult, index) => {
        const inputName = `${FormNameEnum.registers}.${registerIndex}.rows.${rowIndex}.children.${index}.value`
        return {
          key: ocrResult.ocrFormat.id,
          cells: convertChildCells(ocrResult),
          inputCell: {
            name: inputName,
            placeholder,
            defaultValue: getReadItemDefaultValue(
              result.results.manualInputValue,
              result.results.ocrValue,
              ocrResult.result.ocrValue,
              isManualInputStepMissing,
              ocrResult.result.definiteValue
            ),
          },
        }
      })
    }

    const mapResultsToDSLs = (
      results: SalesReportCalculationResult[]
    ): CalculationDSL[] => {
      return results.map((result) => {
        const calculationItem = {
          id: result.calculationItem.id,
          name: result.calculationItem.name,
        }
        const readItems = result.ocrFormatResults.map((formatResult) => {
          return {
            id: formatResult.ocrFormat.id,
            name: formatResult.ocrFormat.readItem,
          }
        })

        return {
          dsl: result.calculationDsl?.dsl || '',
          calculationItem,
          readItems,
        }
      })
    }

    const checkError = (
      result: SalesReportCalculationResult
    ): boolean | undefined => {
      if (result.inputConsistencyResult === undefined) {
        return undefined
      }
      if (
        result.results.manualInputValue === null &&
        result.results.ocrValue === 0
      ) {
        return false
      }
      return !result.inputConsistencyResult
    }

    const convertToRows = (
      registerIndex: number,
      results: SalesReportCalculationResult[]
    ): RowWithSum[] => {
      const dsls = mapResultsToDSLs(results)
      const textResults = parseCalculationDSLsToText(dsls)
      return results.map((result, rowIndex) => {
        const inputName = `${FormNameEnum.registers}.${registerIndex}.rows.${rowIndex}.value`
        const { definiteValue, manualInputValue } = result.results
        const hasDiff = hasDifference(definiteValue, manualInputValue)
        return {
          key: result.calculationItem.id,
          cells: convertToCells(result),
          childRows: convertChildRows(registerIndex, rowIndex, result),
          // NOTE: ['=', 'name', '+', 'name', ...] to '= name + name...'
          tooltipText:
            textResults[rowIndex].text.length === 0
              ? undefined
              : textResults[rowIndex].text.join(' '),
          error: checkError(result),
          inputCell: {
            name: inputName,
            placeholder,
            defaultValue: getCalculationDefaultValue(
              result.results.manualInputValue,
              result.results.ocrValue,
              result.results.definiteValue
            ),
          },
          hasDiff,
        }
      })
    }

    return reports.map((report, registerIndex) => {
      // 組織ごとの非表示項目をフィルタリング
      const filteredResults = filterHiddenCalculationItems(
        report.calculationResults,
        orgCode
      )
      const { highlights, rowColorIndices } =
        extractHighlightsWithColorIndex(filteredResults)
      // rowColorIndices のキーは calculationItem.id なので、行インデックスに依存しない
      const rows = convertToRows(registerIndex, filteredResults).map((row) => ({
        ...row,
        // NOTE: React key にレジIDを含める。calculationItem.id は全テナント共通のため、
        // key が同じだとテナント遷移で入力欄が再マウントされず、直前テナントの表示値・
        // フォーム値が残ったままになる (Issue #3685)。
        key: `${report.id}-${row.key}`,
        highlightColorIndex: rowColorIndices.get(row.key),
      }))
      return {
        key: report.id,
        calculationResult: report.consistencyResult,
        rows,
        concatOriginal: report.receiptImage?.concatOriginal,
        separatedOriginal: report.receiptImage?.separatedOriginal,
        resized: report.receiptImage?.resized,
        isPdf: report.receiptImage?.isPdf,
        tenantRegister: report.tenantRegister,
        highlights,
      }
    })
  },
  convertToDefiniteValues(tenantSalesReports, inputValues, orgCode) {
    // NOTE: inputValuesが前のテナントの入力値を持っているため、tenantSalesReportsを軸に作成する
    return tenantSalesReports.map((report, reportIndex) => {
      const formValues = inputValues.registers[reportIndex]
      // API送信時は全項目を送信（フィルタリングしない）
      // 非表示項目は既存の確定値または0を使用
      const allResults = report.calculationResults

      let formRowIndex = 0 // フォームの行インデックス（hidden項目をスキップ）

      const calculationItems: CalculationDSLDefiniteValue[] = allResults.map(
        (calcItem) => {
          if (
            isHiddenCalculationItemByPolicy(orgCode, calcItem.calculationItem)
          ) {
            // 非表示項目は既存の確定値または0を使用
            const ocrFormats: OCRFormatDefiniteValue[] =
              calcItem.ocrFormatResults.map((ocrResult) => ({
                id: ocrResult.ocrFormat.id,
                definiteValue: ocrResult.result.definiteValue ?? 0,
              }))

            return {
              id: calcItem.results.definiteId,
              calculationItemId: calcItem.calculationItem.id,
              definiteValue: calcItem.results.definiteValue ?? 0,
              ocrFormats,
            }
          }

          // 表示項目はフォームから取得
          const calcVal = formValues?.rows[formRowIndex]
          formRowIndex++

          const ocrFormats: OCRFormatDefiniteValue[] = calcVal?.children
            ? calcVal.children
                .map((ocrVal, ocrIndex) => {
                  const ocrFormat = calcItem.ocrFormatResults[ocrIndex]
                  return {
                    id: ocrFormat?.ocrFormat?.id ?? '',
                    // TODO: Validation で不要になる
                    definiteValue: Number(ocrVal.value || 0),
                  }
                })
                .filter((child) => Boolean(child.id))
            : []

          return {
            id: calcItem.results.definiteId,
            calculationItemId: calcItem.calculationItem.id,
            // TODO: Validation で不要になる
            definiteValue: Number(calcVal?.value || 0),
            ocrFormats,
          }
        }
      )

      return {
        id: report.id,
        calculationItems,
      }
    })
  },
  validateInputValues(inputValues) {
    // NOTE: 空配列は [].every() が true を返すため明示的に弾く。
    // テナント遷移直後のリセット済みフォームで確定できてしまうのを防ぐ (Issue #3685)。
    if (!inputValues.registers || inputValues.registers.length === 0)
      return false
    // NOTE: 全ての入力値が埋まっていれば true

    return inputValues.registers.every((register) => {
      return register.rows.every((row) => {
        const parent = isNumber(row.value)
        if (!row.children) {
          return parent
        }
        return (
          parent &&
          row.children.every((child) => {
            return isNumber(child.value)
          })
        )
      })
    })
  },
  genReportMessages(report, orgCode) {
    if (!report?.registers) return []

    const reportMessages: ReportMessage[] = []
    dayjs.locale('ja')

    const initialReport = report.registers[0]

    // 添付画像を表示しない org では画像を落とし、表示判定にも含めない。
    // 本文なし・画像のみのメモはこの org では表示対象が無くなるため行ごと出さない。
    const visibleImages = (images?: string[]): string[] | undefined =>
      isMemoImageHidden(orgCode) ? undefined : images

    // 補足事項は本文・添付画像のどちらか一方だけでも報告できるため、
    // 本文が無くても画像があれば表示対象にする。
    if (
      shouldDisplayMessage(
        report.reportMessage,
        visibleImages(report.reportMessageImages)
      )
    ) {
      reportMessages.push({
        id: initialReport?.id,
        date: formatDateTime(initialReport.updatedAt),
        reportType: reportTypeEnum.Normal,
        messageBody: report.reportMessage,
        images: visibleImages(report.reportMessageImages),
      })
    }

    if (!report.resentRegisters) return reportMessages
    report.resentRegisters.forEach((resentRegister) => {
      if (
        shouldDisplayMessage(
          resentRegister.resentMessage,
          visibleImages(resentRegister.resentMessageImages)
        )
      ) {
        reportMessages.push({
          id: resentRegister.id,
          date: formatDateTime(resentRegister.updatedAt),
          reportType: reportTypeEnum.Resent,
          reportIndex: resentRegister.index,
          messageBody: resentRegister.resentMessage,
          images: visibleImages(resentRegister.resentMessageImages),
        })
      }
    })
    return reportMessages.sort((a, b) => {
      return dayjs(a.date, DATE_FORMAT).isBefore(dayjs(b.date, DATE_FORMAT))
        ? 1
        : -1
    })
  },
  genSelectDateOptions(report) {
    if (!report?.registers) return []

    const options: Option[] = []
    dayjs.locale('ja')

    const initialReport = report.registers[0]

    options.push({
      value: initialReport?.id,
      title: formatDateTime(initialReport?.updatedAt),
    })

    if (!report.resentRegisters) return options
    report.resentRegisters.forEach((resentRegister) => {
      options.push({
        value: resentRegister.id,
        title: formatDateTime(resentRegister.updatedAt),
      })
    })
    return options.sort((a, b) => {
      return dayjs(a.title, DATE_FORMAT).isBefore(dayjs(b.title, DATE_FORMAT))
        ? 1
        : -1
    })
  },
  findSelectedResentImage(id, report) {
    if (!report?.registers) return undefined
    if (report.registers[0].id === id) return undefined
    if (!report.resentRegisters) return undefined
    return (
      report.resentRegisters.find((resentRegister) => resentRegister.id === id)
        ?.receiptImage || undefined
    )
  },
  convertResentImages(resentRegisters) {
    if (resentRegisters.length === 0) return []

    return resentRegisters
      .map((resentRegister) => {
        if (!resentRegister.receiptImage) return undefined
        return {
          concatOriginal: resentRegister.receiptImage.concatOriginal,
          separatedOriginal: resentRegister.receiptImage.separatedOriginal,
          resized: resentRegister.receiptImage.resized,
        }
      })
      .filter((resentImage) => resentImage) as ZoomableReceiptImageProps[]
  },
}

export default Presenter
